import axios, {AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import {getConfig} from './Config';
import authService from "../service/AuthService";
import logger from "../utils/logger";
import {ApiErrorBody, emitUpgradePrompt, shouldPromptUpgrade} from "./upgradePrompt";

/**
 * Публикует глобальный промпт апгрейда, если ответ об ошибке этого требует (CTHS-21, FR-3/FR-4).
 * Решение — в чистой {@link shouldPromptUpgrade}; здесь только эмиссия события.
 */
const maybeEmitUpgradePrompt = (status: number | undefined, body: ApiErrorBody | undefined): void => {
    if (!shouldPromptUpgrade(status, body)) {
        return;
    }
    emitUpgradePrompt({
        title: body?.error || 'Нужен апгрейд тарифа',
        description: body?.description,
    });
};

interface RetryConfig extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

/**
 * Сериализует query-параметры в URLSearchParams единым для приложения способом:
 * массивы раскладываются повторяющимися ключами (`key=v1&key=v2`) — это
 * задокументированный контракт, который Spring связывает в `@RequestParam List<...>`,
 * и единый формат для всех списочных фильтров (`exclude`, `categoryIds`, `colorIds`…).
 * `null`/`undefined` значения пропускаются. Вынесено сюда, чтобы не дублировать
 * сборку параметров между страницами (OutfitPage, ClothingList).
 */
export const toQueryParams = (params: Record<string, unknown>): URLSearchParams => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
        if (val === null || val === undefined) {
            return;
        }
        if (Array.isArray(val)) {
            val.forEach((v) => search.append(key, String(v)));
        } else {
            search.append(key, String(val));
        }
    });
    return search;
};

/**
 * Часовой пояс пользователя для «дневных» правил бэкенда (одна носка в сутки, dead-stock,
 * «сегодня» в уведомлениях): сервер считает сутки в этом поясе (ClientZoneResolver), иначе
 * вечерняя отметка «перетекала» бы в соседний день по UTC. Битое/недоступное значение —
 * не отправляем, бэкенд падает на UTC.
 */
export const clientTimeZone = (): string | undefined => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
        return undefined;
    }
};

/**
 * Идентификатор запроса для сквозной корреляции: по нему клиентский сбой находится в логах
 * бэкенда без гадания по времени. `crypto.randomUUID` есть не везде (старый Safari, jsdom
 * без полифилла), поэтому запасной путь — случайная строка того же назначения.
 */
export const newRequestId = (): string => {
    try {
        const cryptoApi = globalThis.crypto as Crypto | undefined;
        if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
            return cryptoApi.randomUUID();
        }
    } catch {
        // окружение без crypto — уходим на запасной путь
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const buildInstance = (): AxiosInstance => {
    const timeZone = clientTimeZone();
    const instance = axios.create({
        baseURL: getConfig('apiBaseUrl'),
        // Auth tokens are httpOnly cookies — send them automatically with every request.
        withCredentials: true,
        headers: {
            'Content-Type': 'application/json',
            ...(timeZone ? {'X-Timezone': timeZone} : {}),
        },
    });

    // Идентификатор запроса проставляем на отправке, а не в общих заголовках инстанса:
    // общий заголовок был бы один на все запросы и корреляции не давал. Явно заданный
    // вызывающим кодом идентификатор не перетираем — он может приходить из внешнего контекста.
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        if (!config.headers?.['X-Request-Id']) {
            config.headers?.set?.('X-Request-Id', newRequestId());
        }
        return config;
    });

    // 401 handling: try one cookie refresh and replay the request.
    instance.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as RetryConfig;
            const status = error?.response?.status;
            if (status === 401 && originalRequest && !originalRequest._retry) {
                originalRequest._retry = true;
                try {
                    await authService.updateToken();
                    return instance(originalRequest);
                } catch (e) {
                    logger.debug("AxiosSupport: token refresh on 401 failed, logging out", {error: (e as Error)?.message});
                    await authService.logout();
                }
            }
            // 402/403-апгрейд: ненавязчивый глобальный промпт «нужен апгрейд» (CTHS-21).
            // Промис всё равно reject-ится — вызывающий код обрабатывает ошибку как обычно.
            maybeEmitUpgradePrompt(status, error?.response?.data as ApiErrorBody | undefined);
            return Promise.reject(error);
        }
    );

    return instance;
};

/**
 * Единый клиент приложения. Раньше фабрика создавала новый инстанс с новой парой
 * интерсепторов на каждый вызов (а вызовов около двухсот): каждый экран заводил свой
 * обработчик 401, и один просроченный токен на странице с пятью запросами запускал пять
 * независимых попыток refresh. Инстанс один — набор интерсепторов тоже один.
 */
let clientPromise: Promise<AxiosInstance> | null = null;

/**
 * Контракт сохранён намеренно: функция остаётся асинхронной и вызывается как раньше
 * (`const api = await createAxiosInstance()`), поэтому ни одна страница и ни один тестовый
 * мок не переписываются. Изменилось только то, что за вызовом стоит общий инстанс.
 */
const createAxiosInstance = async (): Promise<AxiosInstance> => {
    clientPromise ??= Promise.resolve(buildInstance());
    return clientPromise;
};

/**
 * Сброс кэша клиента. Нужен тестам и смене конфигурации: `baseURL` читается один раз при
 * создании инстанса, и без сброса он протекал бы из предыдущего окружения.
 */
export const resetApiClient = (): void => {
    clientPromise = null;
};

export default createAxiosInstance;
